import {
  Button,
  Col,
  Descriptions,
  Form,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/lib/table';
import { DateInput, FormInput } from 'components/common/FormItem/FormItems';
import Reload from 'components/common/reload/Reload';
import dayjs from 'dayjs';
import { getLedgerRefLink } from 'modules/Reports/ledgers/utils/ledgerUtils';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import { selectUser } from '../../../../auth/states/userSlice';
import { IPermission } from '../../../../common/types/commonTypes';
import { GetPayMethod } from '../../../../common/utils/CommonUtils';
import { debounce, Fixed2 } from '../../../../common/utils/common.utils';
import usePagination from '../../../../common/utils/usePagination';
import useUrlParams from '../../../../common/utils/useUrlParams';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import LoadingIndicator from '../../../../components/common/spinner/LoadingIndicator';
import { formatDateRangeArray, parseDateRangeObject } from '../../../../helpers/healper';
import { PMReceipt } from '../../Types/MoneyReceiptTypes';
import {
  useApprovedPMReceiptMutation,
  useGetAllPartialMoneyReceiptsQuery,
} from '../../api/endpoints/moneyReceiptEndpoints';

type Props = { permission?: IPermission };

const PartialMoneyReceipt = ({ permission }: Props) => {
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };
  const [columnIndex, setColumnIndex] = useState<number | null>(null);
  const user = useAppSelector(selectUser);

  const {
    data: moneyReceipts,
    isFetching,
    isLoading,
    refetch,
  } = useGetAllPartialMoneyReceiptsQuery({
    ...getAllUrlParams(),
    page,
    size,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const handleView = (record: PMReceipt) => {
    setSelectedRow(record);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setSelectedRow(null);
    setIsModalOpen(false);
  };
  const statusColors: Record<string, string> = {
    SUCCESS: 'green',
    PENDING: 'orange',
    CANCELED: 'red',
    REFUNDED: 'blue',
  };
  const columns: ColumnsType<any> = [
    {
      title: 'SL.',
      render: (_, __, index) => (
        <>{((pagination.current || 1) - 1) * (pagination.pageSize || 50) + 1 + index}</>
      ),
    },
    {
      title: 'Payment Date',
      dataIndex: 'receipt_payment_date',
      key: 'receipt_payment_date',
      align: 'left',
      render: (curr) => curr && dayjs(curr).format('DD-MM-YYYY'),
    },
    // {
    //   title: 'Invoice No',
    //   dataIndex: 'invoice_no',
    //   key: 'invoice_no',
    //   align: 'left',
    // },
    {
      title: 'Voucher',
      dataIndex: 'invoice_no',
      render: (filed, rec) => {
        const link = getLedgerRefLink(rec.invoice_no, rec.receipt_invoice_id);
        return link ? (
          <Link target='_blank' to={link}>
            {filed}
          </Link>
        ) : (
          <span>{filed}</span>
        );
      },
      key: 'invoice_no',
    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (_, record) => (
        <Link
          to={
            record.receipt_client_id
              ? `/reports/client_ledger?client_query=${record.receipt_client_id}`
              : `/reports/combined_ledger?id=${record.combined_id}`
          }
        >
          <span>{record.client_name}</span>
        </Link>
      ),
      align: 'left',
    },
    {
      title: 'Payment Method',
      key: 'receipt_payment_to',
      dataIndex: 'receipt_payment_to',
      render: (field) => <GetPayMethod field={field} />,
    },
    {
      title: 'Category',
      dataIndex: 'invoice_category',
      key: 'invoice_category',
    },
    {
      title: 'Status',
      dataIndex: 'receipt_status',
      key: 'receipt_status',
      render: (status: string) => {
        const color = statusColors[status?.toUpperCase()] || 'default';
        return <Tag color={color}>{status || '-'}</Tag>;
      },
    },

    {
      title: 'Received By',
      key: 'receipt_creator',
      dataIndex: 'receipt_creator',
      render: (val) => val || '-',
    },
    {
      title: 'Received Amount',
      dataIndex: 'receipt_total_amount',
      key: 'receipt_total_amount',
      align: 'right',
      render: (curr) => Fixed2(curr).toLocaleString(),
    },
    {
      title: 'Action',
      key: 'operation',
      width: 120,
      render: (_, record) => (
        <Space size='small'>
          <Button size='small' type='primary' onClick={() => handleView(record)}>
            View
          </Button>
        </Space>
      ),
    },
  ];

  const dataWithKey = moneyReceipts?.data?.data?.map((item, index) => ({
    key: String(index),
    ...item,
  }));

  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search,
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);

  const [approvedPMReceipts, { isSuccess: approvedPM }] = useApprovedPMReceiptMutation();
  useEffect(() => {
    if (approvedPM) {
      handleClose(); // ✅ Close modal after approval
      message.success('Approved Successfully');
      refetch();
    }
  }, [approvedPM]);
  return (
    <>
      {(isLoading || isFetching) && <LoadingIndicator />}

      <BreadCrumb arrOfOption={['Partial Money Receipts']} />

      {/* HEADER */}
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 10 }} justify={'space-between'}>
        <Col span={12}>
          <Row gutter={10}>
            <Col xs={24} sm={12} md={8} lg={8} style={{ display: 'flex' }}>
              <Col>
                <Reload refetch={refetch} />
              </Col>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <DateInput
                name={'date_range'}
                rangePicker
                size={8}
                noStyle
                onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
              />
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
            </Row>
          </Form>
        </Col>
      </Row>

      <div>
        <span style={{ float: 'left', marginBottom: '1rem' }}>
          You have total
          <strong>
            {' '}
            {moneyReceipts?.count ? moneyReceipts?.count : moneyReceipts?.data?.data?.length}{' '}
          </strong>
          rows
        </span>
        <Table
          scroll={{ x: 'max-content' }}
          size='small'
          bordered
          rowKey={(e) => e.receipt_vouchar_no}
          columns={columns}
          dataSource={dataWithKey}
          pagination={changePagination(moneyReceipts?.count)}
        />
      </div>

      {/* VIEW MODAL */}
      <Modal
        title={`Partial Money Receipt of Invoice No: ${selectedRow?.invoice_no}`}
        open={isModalOpen}
        onCancel={handleClose}
        footer={[
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'right' }}>
            <Button key='close' danger type='primary' onClick={handleClose}>
              Close
            </Button>
            {selectedRow?.receipt_status !== 'SUCCESS' && selectedRow?.receipt_status !== null && (
              <Popconfirm
                title='Approved Receipt'
                description='Are you sure to approved this receipt?'
                onConfirm={() => approvedPMReceipts(selectedRow?.receipt_invoice_id)}
              >
                <Button type='primary' style={{ background: 'green' }} key='update'>
                  Approved
                </Button>
              </Popconfirm>
            )}
          </div>,
        ]}
        width={700}
      >
        {selectedRow && (
          <Descriptions bordered column={2} size='small'>
            <Descriptions.Item label='Invoice No' span={1}>
              {selectedRow.invoice_no}
            </Descriptions.Item>
            <Descriptions.Item label='Payment Date' span={1}>
              {dayjs(selectedRow.receipt_payment_date).format('DD-MM-YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label='Client Name' span={2}>
              {selectedRow.client_name}
            </Descriptions.Item>
            <Descriptions.Item label='Payment Method' span={1}>
              <GetPayMethod field={selectedRow.receipt_payment_to} />
            </Descriptions.Item>
            <Descriptions.Item label='Category' span={1}>
              {selectedRow.invoice_category}
            </Descriptions.Item>

            <Descriptions.Item label='Received Amount' span={1}>
              {Fixed2(selectedRow.receipt_total_amount).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label='Status' span={1}>
              {selectedRow.receipt_status}
            </Descriptions.Item>
            <Descriptions.Item label='Created By' span={2}>
              {selectedRow.receipt_creator || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default PartialMoneyReceipt;
